import type { TExample } from '@atlas/uicorn/preview/modules';
import * as React from 'react';
import { InputNumber as AntInputNumber, type InputNumberProps as TAntInputNumberProps } from 'antd';
import styles from './NumberInput.module.css';
import cn from 'classnames';

const NumberInput = React.memo(
    React.forwardRef<TNumberInputRef, TNumberInputProps>(function NumberInput(props, ref): React.ReactElement {
        const { hideControls, autoComplete = 'off', autoCorrect = 'off', className, ...inputProps } = props;
        return (
            <AntInputNumber
                {...inputProps}
                className={cn(styles.numberInput, className)}
                controls={hideControls ? false : undefined}
                ref={ref}
                autoComplete={autoComplete}
                autoCorrect={autoCorrect}
            />
        );
    }),
);

export default NumberInput;

// #region Types

export type TNumberInputProps = Pick<
    TAntInputNumberProps,
    | 'className'
    | 'style'
    | 'value'
    | 'defaultValue'
    | 'onChange'
    | 'onKeyDown'
    | 'placeholder'
    | 'disabled'
    | 'autoFocus'
    | 'min'
    | 'max'
    | 'required'
    | 'precision'
    | 'step'
    | 'autoComplete'
    | 'autoCorrect'
> & { hideControls?: boolean };

export type TNumberInputRef = HTMLInputElement;

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        return <NumberInput disabled={props.disabled} onChange={props.onChange} />;
    },
    controls: {
        disabled: 'boolean',
    },
    handlers: {
        onChange: 'console',
    },
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('NumberInput', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
